import { HubConnectionState } from "@microsoft/signalr";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { API_URL, ENDPOINT } from "../configs/api";
import { useAuthStore, useClassStore, useUserStore } from "../stores";
import { SignalRMethodName } from "../types/signalr";
import { ClassRequestStatus, PracticeQuizState } from "../types/class";
import { Session } from "../types/session";
import { toast } from "vue3-toastify";
import { QuizCurrentQuestion, QuizState } from "../types/quiz";
import { AssessmentType } from "../types/assessment";
import * as Sentry from '@sentry/vue'

const connection = new HubConnectionBuilder()
    .withUrl(API_URL.iclass + ENDPOINT.iclass.hub, {
        accessTokenFactory: () => {
            const auth = useAuthStore()
            return auth.accessToken || ''
        }
    }).build()
connection.keepAliveIntervalInMilliseconds = 5000
connection.serverTimeoutInMilliseconds = 10000

export { connection }

export async function connect(): Promise<HubConnectionState> {
    if (connection.state === HubConnectionState.Disconnected) {
        await connection.start().catch(error => {
            console.log(error)

            // check authorization
            if (error.message.includes('401')) {
                // notify error
                toast.error(error.message)

                const auth = useAuthStore();
                auth.logout()
            }
        })
    }
    return connection.state
}

export function disconnect() {
    connection.stop()
}

function getSignalRMethodIndex(methodName: SignalRMethodName): number {
    const user = useUserStore()
    const classStore = useClassStore()

    Sentry.captureMessage(methodName, {
        level: 'log',
        tags: {
            system: classStore.selectedClassDetails?.system,
            classId: classStore.selectedClassDetails?.id,
            role: user.userRole,
            actionType: 'send'
        }
    })

    const values = Object.values(SignalRMethodName)
    return values.indexOf(methodName)
}

export async function getCurrentQuestion(roomName: string): Promise<QuizCurrentQuestion | undefined> {
    if (!roomName) return;
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.getCurrentQuestion
    return await connection.invoke(methodName, roomName)
}

export function joinRoom(roomName: string, roomId: number) {
    if (!roomName) return;
    if (connection.state !== HubConnectionState.Connected) return;

    const user = useUserStore()
    const methodName = SignalRMethodName.connect
    const messageId = getSignalRMethodIndex(methodName)
    const userId = String(user.userInfo?.id)
    const userRole = user.userRole
    const username = user.username
    connection.send(methodName, messageId, userId, userRole, username, roomName, roomId)
}

export function leaveRoom(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.leaveRoom
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export function studentChooseGroup(roomName: string, groupId: number) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.chooseGroup
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, groupId)
}

export function teacherChangeGroup(roomName: string, classId: number, groupId: number, userId: number) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.changeGroup
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, classId, groupId, userId)
}

export function studentAskJoinRoom(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.askJoinRoom
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export async function approveJoin(username: string, roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.approveJoin
    const messageId = getSignalRMethodIndex(methodName)
    await connection.invoke(methodName, messageId, ClassRequestStatus.approved, username, roomName)
}

export function startClassRoom(roomName: string, classId: number) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.startClassRoom
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, classId)
}

export function endClassRoom(roomName: string, classId: number) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.endClassRoom
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, classId)
}

export function requestRaiseHand(roomName: string, status: boolean) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.requestRaiseHand
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, status)
}

export function removeRequestRaiseHand(roomName: string, usernames: string[]) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.removeRequestRaiseHand
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, usernames)
}

export function raiseHand(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.raiseHand
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export function removeRaiseHand(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.removeRaiseHand
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export function lockScreen(roomName: string, status: boolean) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.lockScreen
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, status)
}

export function rewardPoint(roomName: string, username: string, point: number, session: Session, id: number | null) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.rewardPoint
    const messageId = getSignalRMethodIndex(methodName)
    let sendSession = session.toString()
    if (session === Session.vocab) {
        sendSession = 'vocabulary'
    }
    if (session === Session.practice2) {
        sendSession = 'practice'
    }
    if (session === Session.custom) {
        sendSession = 'custom_section'
    }
    connection.send(methodName, messageId, roomName, username, point, sendSession, id)
}

export function rewardPointGroup(roomName: string, groupId: number, point: number, session: Session, id: number | null) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.rewardPointGroup
    const messageId = getSignalRMethodIndex(methodName)
    let sendSession = session.toString()
    if (session === Session.vocab) {
        sendSession = 'vocabulary'
    }
    if (session === Session.practice2) {
        sendSession = 'practice'
    }
    if (session === Session.custom) {
        sendSession = 'custom_section'
    }
    connection.send(methodName, messageId, roomName, groupId, point, sendSession, id)
}

export function controlScreen(roomName: string, session: Session | null) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.controlScreen
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, session)
}

export function setPracticeState(roomName: string, state: PracticeQuizState) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.setPracticeState
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, state)
}

export function updateQuizUrl(roomName: string, classId: number, url: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.updateQuizUrl
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, classId, url)
}

export function updateLectureQuestion(roomName: string, classId: number, question: string) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.updateLectureQuestion
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, classId, question)
}

export function selectStudentAnswer(roomName: string, vocabId: string, usernames: string[]) {
    if (connection.state !== HubConnectionState.Connected) return;

    const methodName = SignalRMethodName.selectStudentAnswer
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, vocabId, usernames)
}

export function startQuiz(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.startQuiz
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export function finishedQuiz(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.finishedQuiz
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export function settingQuiz(roomName: string, showCorrectAnswer: number, showRanking: number, questionTime: number) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.settingQuiz
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, showCorrectAnswer, showRanking, questionTime)
}

export function changeQuizQuestion(roomName: string, questionId: number, totalTime: number) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.changeQuizQuestion
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, questionId, totalTime)
}
export function finishedCurrentQuestion(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.finishedCurrentQuestion
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}
export function reviewCurrentQuestion(roomName: string) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.reviewCurrentQuestion
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName)
}

export function answerQuestion(roomName: string, roomId: number, questionId: number, setId: number, type: AssessmentType, answers: string[], startTime: number) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.answerQuestion
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, roomId, questionId, setId, type, answers, startTime)
}

export function updateQuizState(roomName: string, state: QuizState) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.updateQuizState
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, state)
}

export function updateLectureSettings(roomName: string, studentViewAnswer: number) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.settingLecture
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, studentViewAnswer)
}

export function submitLecture(roomName: string, status: boolean) {
    if (connection.state !== HubConnectionState.Connected) return;
    const methodName = SignalRMethodName.submitLecture
    const messageId = getSignalRMethodIndex(methodName)
    connection.send(methodName, messageId, roomName, status)
}